import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Button, InputBase, Typography } from '@mui/material'
import { Mic as MicIcon } from '@mui/icons-material'

import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { RADIUS } from '@/shared/styles/radii'
import {
  FONT_WEIGHT,
  UI_FONT_SIZE,
  typeScale,
} from '@/shared/styles/typography'
import { useNavigate } from 'react-router-dom'

import { PatientInfoBar } from '@/apps/latest/components/molecules/PatientInfoBar'
import { RecordingContextMenu } from '@/apps/latest/components/molecules/RecordingContextMenu'
import { RecordingModeMenu } from '@/apps/latest/components/molecules/RecordingModeMenu'
import { PharmacistMenu } from '@/apps/latest/components/molecules/PharmacistMenu'
import { MeetingBlock } from '@/apps/latest/components/organisms/MeetingBlock'
import { Layout } from '@/apps/latest/components/templates/Layout'
import { ActiveRecordingProvider } from '@/apps/latest/shared/contexts/ActiveRecordingContext'
import { useActiveRecordingEmit } from '@/apps/latest/shared/recording-status/useActiveRecordingEmit'
import { usePollEvent } from '@/apps/latest/shared/recording-status/usePollEvent'
import { useRecordingStatus } from '@/apps/latest/shared/recording-status/useRecordingStatus'
import { useSessionPharmacist, usePharmacistsQuery } from '@/features/auth'
import { usePatchYakurekiEventMutation } from '@/features/yakureki'
import {
  DEFAULT_RECORDING_PRESET,
  type RecordingPreset,
} from '@/features/yakureki/recording/streaming/recordingPreset'
import { type SessionType } from '@/shared/types/yakurekiCore'

// Phase A フリー録音: 患者紐付けなしの新規録音 page.
// stream/start を is_free_recording=true で叩くと back が空 Event を 1 件 auto-create.
// 録音完了後に navigate(/soap/:id, replace) で既存 IndexPage に流入 → ViewStatus='generating' 表示.
const NewRecordingPage: FC = () => {
  const navigate = useNavigate()

  const { pharmacists } = usePharmacistsQuery()
  const pharmacistList = useMemo(
    () => (pharmacists?.pharmacists ?? []).filter((p) => p.is_own),
    [pharmacists]
  )
  const { sessionPharmacistId, setSessionPharmacistId } =
    useSessionPharmacist(pharmacistList)
  const patchEventMutation = usePatchYakurekiEventMutation()

  // start で back が auto-create した event id (= 1 件). engine 経由で観測.
  // フリー録音は streaming 固定 (useRecordingStatus.startRecording が固定する) のため、
  // 録音が始まっていれば stream/start の auto-create で必ず存在する.
  const [currentEventId, setCurrentEventId] = useState<string | null>(null)

  const onFallbackCommit = useCallback(
    async (blob: Blob): Promise<void> => {
      if (!currentEventId) {
        throw new Error('event id missing during fallback commit')
      }
      const file = new File([blob], 'audio.mp4', {
        type: blob.type || 'audio/mp4',
      })
      await patchEventMutation.mutateAsync({
        audioSource: file,
        label: '',
        yakurekiId: currentEventId,
        pharmacistId: sessionPharmacistId || undefined,
        memoItems: [],
      })
    },
    [patchEventMutation, sessionPharmacistId, currentEventId]
  )

  // L1 (server canonical event). auto-create された event を録音中から終端まで同じ
  // cadence 規則で追う (= 判定は usePollEvent が単独で持つ).
  const { data: event } = usePollEvent({ yakurekiId: currentEventId })

  const recordingStatus = useRecordingStatus({
    event: event ?? undefined,
    onFallbackCommit,
  })
  // method 呼び出しの receiver を deps に含めず callback を安定させるため destructure する.
  const { startRecording } = recordingStatus
  useActiveRecordingEmit(recordingStatus)

  // engine が auto-create で得た event id を state に反映 (= "Adjusting state when props change").
  const incomingEventId = recordingStatus.engine.recordingEventIds[0] ?? null
  if (incomingEventId && incomingEventId !== currentEventId) {
    setCurrentEventId(incomingEventId)
  }
  // engine が idle に戻って ids が無い = 破棄 / 再開の期限切れ / 失敗後の片付け. いずれも
  // この画面に表示すべき event が無いので録音前の入力画面に戻す (hasStarted = currentEventId !== null).
  // フリー録音の event は破棄で back が器ごと削除する (ADR-0039) ので、 id を握ったままだと
  // 消えた event を poll し続けることになる.
  // 完了時は engine.state='completed' なのでここは通らず、 下の navigate effect が先に走る.
  if (
    currentEventId &&
    !incomingEventId &&
    recordingStatus.engine.state === 'idle'
  ) {
    setCurrentEventId(null)
  }

  // start 前に user が入力する患者名 (= 任意).
  const [customLabel, setCustomLabel] = useState('')
  // 録音開始時に選ぶ生成モード (標準/深掘り). intent を back に送るだけ.
  const [preset, setPreset] = useState<RecordingPreset>(
    DEFAULT_RECORDING_PRESET
  )
  // 来局/在宅. 訪問診療同行など処方箋発行前の訪問は患者を選べないため、
  // 新規録音の側で在宅を選べる必要がある (β のみ pill が出る).
  const [sessionType, setSessionType] = useState<SessionType>('walk_in')

  const handleStartRecording = useCallback(() => {
    const trimmed = customLabel.trim()
    startRecording({
      yakurekiIds: [],
      isFreeRecording: true,
      pharmacistId: sessionPharmacistId || undefined,
      label: trimmed || undefined,
      preset,
      sessionType,
    })
  }, [startRecording, sessionPharmacistId, customLabel, preset, sessionType])

  // 録音完了 (= engine.state='completed') で SOAP review 画面に遷移.
  // isSlowTranscription は engine (= 本 page の hook instance) に閉じており遷移で失われる
  // ため、 history state に載せて IndexPage の低速バナー表示に引き継ぐ.
  // finalized は「この録音は finalize ack 済み」の証拠 (ADR-0041): 遷移先の engine は
  // 別 instance で idle なので、 サーバが processing を書く前の 1 poll (cache は recording)
  // を generating と読むにはこの hint が要る. 無いと mic が一瞬戻る.
  useEffect(() => {
    if (recordingStatus.engine.state !== 'completed' || !currentEventId) return
    navigate(`/soap/${currentEventId}`, {
      replace: true,
      state: {
        finalized: true,
        ...(recordingStatus.engine.isSlowTranscription
          ? {
              slowTranscription: true,
              intendedBatch: recordingStatus.engine.isIntendedBatch,
            }
          : {}),
      },
    })
  }, [
    recordingStatus.engine.state,
    recordingStatus.engine.isSlowTranscription,
    recordingStatus.engine.isIntendedBatch,
    currentEventId,
    navigate,
  ])

  const hasStarted = currentEventId !== null

  return (
    <Layout breadcrumb={['新規録音']}>
      {hasStarted && event ? (
        <>
          <Box sx={{ mb: 3 }}>
            <PatientInfoBar
              event={event}
              recordingStatus={recordingStatus}
              onStartRecording={handleStartRecording}
            />
          </Box>
          <MeetingBlock
            yakurekiId={recordingStatus.engine.recordingEventIds[0] ?? null}
            recordingStatus={recordingStatus}
          />
        </>
      ) : (
        // start 前. 「患者名 : <input>」 の inline label-value pattern.
        <Box sx={{ pt: 4 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              mb: 3,
            }}
          >
            <Typography
              component="label"
              sx={{
                flexShrink: 0,
                width: 80,
                fontSize: UI_FONT_SIZE.label,
                fontWeight: FONT_WEIGHT.medium,
                color: colorTokens.fg.muted,
              }}
            >
              患者名
            </Typography>
            <InputBase
              value={customLabel}
              onChange={(e) => setCustomLabel(e.target.value)}
              fullWidth
              inputProps={{ 'aria-label': '患者名' }}
              sx={{
                flex: 1,
                fontSize: typeScale.body.fontSize,
                lineHeight: typeScale.body.lineHeight,
                color: colorTokens.fg.default,
                px: 1.25,
                py: 0.75,
                borderRadius: `${RADIUS.sm}px`,
                backgroundColor: colorTokens.bg.subtle,
                transition: 'background-color 120ms, box-shadow 120ms',
                '&:hover': {
                  backgroundColor: colorTokens.bg.muted,
                },
                '&.Mui-focused': {
                  backgroundColor: colorTokens.bg.default,
                  boxShadow: `inset 0 0 0 1px ${colorTokens.border.default}`,
                },
              }}
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            <Button
              variant="contained"
              size="medium"
              startIcon={<MicIcon />}
              onClick={handleStartRecording}
            >
              録音開始
            </Button>
            <RecordingModeMenu value={preset} onChange={setPreset} />
            <RecordingContextMenu
              sessionType={sessionType}
              onSessionTypeChange={setSessionType}
            />
            {pharmacistList.length >= 2 && (
              <PharmacistMenu
                pharmacists={pharmacistList}
                value={sessionPharmacistId}
                onChange={setSessionPharmacistId}
              />
            )}
          </Box>
        </Box>
      )}
    </Layout>
  )
}

const LatestNewRecordingPage: FC = () => (
  <ActiveRecordingProvider>
    <NewRecordingPage />
  </ActiveRecordingProvider>
)

export default LatestNewRecordingPage
